import React, { useState, useContext } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Box,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { ContextSession } from '../../../services/ContextSession';
const AvanceTramiteTgu = ({ tramiteId, usuarioId, onAvanceCreado }) => {
  const [nuevoAvance, setNuevoAvance] = useState({
    descripcion: '',
    archivos: []
  });
  const [submittingAvance, setSubmittingAvance] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const sessi = useContext(ContextSession);


  const handleAvanceChange = (e) => {
    const { name, value } = e.target;
    setNuevoAvance(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setNuevoAvance(prev => ({
      ...prev,
      archivos: [...prev.archivos, ...files]
    }));
  };

  const handleRemoveArchivo = (index) => {
    setNuevoAvance(prev => ({
      ...prev,
      archivos: prev.archivos.filter((_, i) => i !== index)
    }));
  };

  const handleClearAvance = () => {
    setNuevoAvance({
      descripcion: '',
      archivos: []
    });
    setError(null);
    setSuccess(null);
  };

  const handleSubmitAvance = async () => {
  if (!nuevoAvance.descripcion.trim()) {
    setError('La descripción del avance es requerida');
    return;
  }

  const token = sessi?.sessionData?.token;
  const usuarioId = sessi?.sessionData?.id;

  if (!token || !usuarioId) {
    setError('No se ha encontrado información de usuario. Por favor, vuelva a iniciar sesión.');
    return;
  }

  setSubmittingAvance(true);
  setError(null);
  setSuccess(null);
  setDisabled(true);

  try {
    // Crear avance
    const avanceResponse = await fetch('https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/avance/crear/', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tramiteid: tramiteId,
        descripcionavance: nuevoAvance.descripcion,
        usuarioid: usuarioId
      })
    });

    if (!avanceResponse.ok) {
      const errorData = await avanceResponse.json().catch(() => ({}));
      throw new Error(errorData.message || `Error ${avanceResponse.status}: ${avanceResponse.statusText}`);
    }

    const avanceData = await avanceResponse.json();
    const avanceId = avanceData.id;

    // Subir archivos si existen
    if (nuevoAvance.archivos && nuevoAvance.archivos.length > 0) {
      for (const archivo of nuevoAvance.archivos) {
        const formData = new FormData();
        formData.append('archivo', archivo);
        formData.append('entity_id', tramiteId);
        formData.append('usuario_id', usuarioId);
        // formData.append('avance_id', avanceId); // Solo si tu backend lo requiere

        const adjuntoResponse = await fetch('https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/adjuntos/subir/', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!adjuntoResponse.ok) {
          console.error('Error al subir archivo adjunto:', archivo.name);
        }
      }
    }

    setSuccess('Avance creado correctamente' + (nuevoAvance.archivos.length > 0 ? ' con archivos adjuntos' : ''));
    handleClearAvance();

    if (onAvanceCreado) {
      onAvanceCreado(avanceData);
    }
  } catch (err) {
    setError(err.message);
  } finally {
    setSubmittingAvance(false);
    setDisabled(false);
  }
};


  return (
    <Paper elevation={2} style={{ padding: '1.3rem', backgroundColor: '#f5f5f5', marginBottom: '1rem' }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
        Agregar Avance
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TextField
        fullWidth
        multiline
        rows={4}
        variant="outlined"
        label="Descripción del avance"
        name="descripcion"
        value={nuevoAvance.descripcion}
        onChange={handleAvanceChange}
        sx={{ mb: 2 }}
        disabled={disabled}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          component="label"
          startIcon={<AttachFileIcon />}
          disabled={disabled}
          sx={{
            minWidth: 115,
            height: 38,
            color: '#1976d2',
            borderColor: '#1976d2',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#e3f2fd',
              borderColor: '#115293',
            },
          }}
        >
          Adjuntar
          <input
            type="file"
            hidden
            multiple
            onChange={handleFileChange}
          />
        </Button>

        <Button
          variant="outlined"
          onClick={handleClearAvance}
          startIcon={<DeleteOutlineIcon />}
          sx={{
            minWidth: 115,
            height: 38,
            color: '#d32f2f',
            borderColor: '#d32f2f',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#ffebee',
              borderColor: '#b71c1c',
            },
          }}
        >
          Limpiar
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmitAvance}
          disabled={submittingAvance || !nuevoAvance.descripcion}
          startIcon={submittingAvance ? <CircularProgress size={20} /> : <AddIcon />}
          sx={{
            minWidth: 115,
            height: 38,
            backgroundColor: '#19438C',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#19438C',
            },
          }}
        >
          {submittingAvance ? 'Guardando...' : 'Guardar '}
        </Button>
      </Box>

      {nuevoAvance.archivos?.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {nuevoAvance.archivos.map((file, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                {file.name}
              </Typography>
              <Button
                size="small"
                color="error"
                onClick={() => handleRemoveArchivo(index)}
              >
                <DeleteIcon />    
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default AvanceTramiteTgu;